import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import VisitCard from "../../components/visits/VisitCard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { todayIST, daysAgoIST, istDayKey } from "../../lib/datetime";
import { Plus, Sparkles, Inbox, Stethoscope } from "lucide-react";
import StickyHeading from "../../components/common/StickyHeading";

/**
 * RC-8. Buckets by the IST calendar day of the value, never by the raw string
 * and never by the viewer's local midnight. `istDayKey` normalises both shapes
 * the API can hand back — a date-only `2026-08-12` and a full instant — so a
 * visit at 00:30 IST (19:00Z the previous day) lands on the day it happened in
 * the company's zone rather than in yesterday's group.
 */
function groupKey(visitDate) {
  const day = istDayKey(visitDate);
  if (!day) return "Older";
  const today = todayIST();
  const yesterday = daysAgoIST(1);
  const weekAgo = daysAgoIST(7);
  if (day === today) return "Today";
  if (day === yesterday) return "Yesterday";
  if (day >= weekAgo) return "This week";
  return "Older";
}

const GROUP_ORDER = ["Today", "Yesterday", "This week", "Older"];

export default function MyVisitsPage() {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    let cancelled = false;
    setLoading(true);
    api
      .get("/doctor-visits", { params })
      .then(({ data }) => !cancelled && setVisits(data))
      .catch((e) => !cancelled && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [from, to]);

  const groups = useMemo(() => {
    const map = {};
    for (const v of visits) {
      const k = groupKey(v.visit_date);
      (map[k] ||= []).push(v);
    }
    return map;
  }, [visits]);

  const todayCount = (groups["Today"] || []).length;
  const weekCount = visits.length;

  return (
    <div className="space-y-6" data-testid="my-visits-page">
      <StickyHeading
        icon={Sparkles}
        label="My Daily Call Report"
        title="My visits"
        subtitle={<>{todayCount} today · {weekCount} total in view</>}
        testId="my-visits-heading"
        actions={
          <Link
            to="/doctor-visits/new"
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-callisto-navy text-white h-11 px-5 text-sm font-semibold hover:bg-callisto-navy/90"
            data-testid="log-visit-cta"
          >
            <Plus className="h-4 w-4" />
            Log a visit
          </Link>
        }
      />

      <div className="bg-white border border-callisto rounded-xl p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center gap-2 flex-1">
          <span className="text-[11px] uppercase tracking-wider text-callisto-muted shrink-0">
            From
          </span>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            max={to || undefined}
            className="h-10 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="my-visits-from"
          />
          <span className="text-[11px] uppercase tracking-wider text-callisto-muted shrink-0">
            To
          </span>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            min={from || undefined}
            className="h-10 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="my-visits-to"
          />
        </div>
        {(from || to) && (
          <Button
            variant="ghost"
            onClick={() => {
              setFrom("");
              setTo("");
            }}
            className="h-10 text-callisto-muted"
          >
            Clear
          </Button>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-xl" />
          ))}
        </div>
      ) : visits.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-8" data-testid="my-visits-groups">
          {GROUP_ORDER.filter((k) => groups[k]?.length).map((k) => (
            <div key={k}>
              <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
                {k} · {groups[k].length}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {groups[k].map((v) => (
                  <VisitCard key={v.id} visit={v} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-callisto rounded-xl p-10 md:p-14 flex flex-col items-center text-center">
      <div className="h-16 w-16 rounded-2xl bg-callisto-navy/5 flex items-center justify-center mb-4">
        <Stethoscope className="h-7 w-7 text-callisto-navy" strokeWidth={1.5} />
      </div>
      <h3 className="text-lg font-bold text-callisto-navy">No visits yet</h3>
      <p className="mt-1 text-sm text-callisto-muted max-w-md">
        Once you log your first doctor visit, it will show up here grouped by date.
      </p>
      <Link
        to="/doctor-visits/new"
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-callisto-navy text-white px-4 py-2.5 text-sm font-semibold hover:bg-callisto-navy/90"
      >
        <Plus className="h-4 w-4" />
        Log your first visit
      </Link>
    </div>
  );
}
